/**
 * Accessibility utilities for WCAG 2.1 Level AA compliance
 */

/**
 * Format time for screen reader announcement
 * Returns a fully descriptive time string like "10:30 AM on Monday, December 16"
 */
export function formatTimeForScreenReader(
  hour: number,
  minute: number,
  date: Date,
  locale: string
): string {
  // Validate date parameter to prevent runtime errors with malformed data
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
    // Fallback for invalid dates - return basic time format
    return `${hour}:${minute.toString().padStart(2, "0")}`;
  }

  // Use the actual slot date to preserve DST context
  const slotDateTime = new Date(date);
  slotDateTime.setHours(hour, minute, 0, 0);

  const safeLocale = (() => {
    try {
      Intl.DateTimeFormat.supportedLocalesOf([locale]);
      return locale || "en";
    } catch {
      return "en";
    }
  })();

  const timeString = slotDateTime.toLocaleString(safeLocale, {
    hour: "numeric",
    minute: "2-digit"
  });

  const dateString = slotDateTime.toLocaleDateString(safeLocale, {
    weekday: "long",
    month: "long",
    day: "numeric"
  });

  return `${timeString} on ${dateString}`;
}

/**
 * Generate a unique ID for accessibility associations
 */
export function generateA11yId(
  prefix: string,
  ...parts: (string | number)[]
): string {
  return `${prefix}-${parts.join("-")}`;
}

/**
 * CSS-in-JS styles for visually hidden but screen reader accessible content
 */
export const srOnlyStyles = {
  position: "absolute" as const,
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap" as const,
  border: 0
};

/**
 * Focus ring styles for high contrast visibility
 * Use with MUI sx prop: sx={{ ...focusRingStyles }}
 */
export const focusRingStyles = {
  "&:focus-visible": {
    outline: "3px solid #005fcc",
    outlineOffset: "2px"
  },
  '[data-theme="dark"] &:focus-visible, [data-mui-color-scheme="dark"] &:focus-visible':
    {
      outline: "3px solid #66b3ff",
      outlineOffset: "2px"
    }
};

/**
 * Keyboard navigation directions
 */
export type NavigationDirection =
  | "up"
  | "down"
  | "left"
  | "right"
  | "home"
  | "end";

/**
 * Get navigation direction from keyboard event
 * Returns null if the key is not a navigation key
 */
export function getNavigationDirection(
  event: React.KeyboardEvent
): NavigationDirection | null {
  switch (event.key) {
    case "ArrowUp":
      return "up";
    case "ArrowDown":
      return "down";
    case "ArrowLeft":
      return "left";
    case "ArrowRight":
      return "right";
    case "Home":
      return "home";
    case "End":
      return "end";
    default:
      return null;
  }
}

/**
 * Calculate next grid position based on navigation direction
 */
export function getNextGridPosition(
  currentRow: number,
  currentCol: number,
  rowCount: number,
  colCount: number,
  direction: NavigationDirection
): { row: number; col: number } {
  switch (direction) {
    case "up":
      return { row: Math.max(0, currentRow - 1), col: currentCol };
    case "down":
      return { row: Math.min(rowCount - 1, currentRow + 1), col: currentCol };
    case "left":
      return { row: currentRow, col: Math.max(0, currentCol - 1) };
    case "right":
      return { row: currentRow, col: Math.min(colCount - 1, currentCol + 1) };
    case "home":
      return { row: 0, col: currentCol };
    case "end":
      return { row: rowCount - 1, col: currentCol };
    default:
      return { row: currentRow, col: currentCol };
  }
}
